/**
 * Predefined "document sets" for the Request Documents form. Selecting a set
 * appends its document types as request items in one click, instead of adding
 * them one at a time. Each item references a document-category code (matched
 * case-insensitively against the tenant's document_categories lookup); if a
 * code isn't configured for the tenant, that item is skipped when applied.
 *
 * These are common accounting/tax bundles. Kept as a static list for now — can
 * be made tenant-configurable later.
 */
export interface DocumentSetItem {
  /** document-category code to match against the tenant lookup */
  code: string;
  /** fallback label if the code isn't in the tenant lookup */
  label: string;
  description?: string;
  isMandatory?: boolean;
}

export interface DocumentSetPreset {
  id: string;
  name: string;
  description: string;
  items: DocumentSetItem[];
}

export const DOCUMENT_SET_PRESETS: DocumentSetPreset[] = [
  {
    id: 'personal_tax_t1',
    name: 'Personal Tax (T1)',
    description: 'Common slips and receipts for a personal tax return',
    items: [
      { code: 't4', label: 'T4 Slips', description: 'Employment income slips', isMandatory: true },
      { code: 't5', label: 'T5 Slips', description: 'Investment income slips' },
      { code: 'rrsp', label: 'RRSP Contribution Receipts' },
      { code: 'medical_receipts', label: 'Medical Receipts' },
      { code: 'donation_receipts', label: 'Donation Receipts' },
    ],
  },
  {
    id: 'corporate_tax_t2',
    name: 'Corporate Tax (T2)',
    description: 'Year-end documents for a corporate return',
    items: [
      { code: 'financial_statements', label: 'Financial Statements', isMandatory: true },
      { code: 'bank_statements', label: 'Bank Statements', isMandatory: true },
      { code: 'gl', label: 'General Ledger' },
      { code: 'payroll_summary', label: 'Payroll Summary' },
    ],
  },
  {
    id: 'bookkeeping',
    name: 'Monthly Bookkeeping',
    description: 'Source documents for a bookkeeping period',
    items: [
      { code: 'bank_statements', label: 'Bank Statements', isMandatory: true },
      { code: 'credit_card_statements', label: 'Credit Card Statements' },
      { code: 'sales_invoices', label: 'Sales Invoices' },
      { code: 'expense_receipts', label: 'Expense Receipts' },
    ],
  },
  {
    id: 'onboarding',
    name: 'New Client Onboarding',
    description: 'Identity and setup documents for a new client',
    items: [
      { code: 'photo_id', label: 'Government-issued Photo ID', isMandatory: true },
      { code: 'business_registration', label: 'Business Registration' },
      { code: 'void_cheque', label: 'Void Cheque' },
      { code: 'prior_returns', label: 'Prior Year Returns' },
    ],
  },
];
